import { defineMessages } from 'react-intl';

/** The copy of the frame of the music video editor (design music-video b1, e1 to e6). */
export const editorMessages = defineMessages({
  // The top bar (b1)
  back: { id: 'app.sml.musicVideo.top.back', defaultMessage: 'Back' },
  songLine: { id: 'app.sml.musicVideo.top.songLine', defaultMessage: '{artist} · music video · {length} · {size}' },
  undo: { id: 'app.sml.musicVideo.top.undo', defaultMessage: 'Undo' },
  redo: { id: 'app.sml.musicVideo.top.redo', defaultMessage: 'Redo' },
  cutMenu: { id: 'app.sml.musicVideo.top.cutMenu', defaultMessage: 'Cut {number} of {count}' },
  cutItem: { id: 'app.sml.musicVideo.top.cutItem', defaultMessage: 'Cut {number}' },
  cutItemMade: { id: 'app.sml.musicVideo.top.cutItemMade', defaultMessage: 'made' },
  cutItemWorking: { id: 'app.sml.musicVideo.top.cutItemWorking', defaultMessage: 'the AI works' },
  cutNew: { id: 'app.sml.musicVideo.top.cutNew', defaultMessage: 'New cut' },
  cutNewHint: { id: 'app.sml.musicVideo.top.cutNewHint', defaultMessage: 'The AI makes a new first cut. No credits.' },
  cutCopy: { id: 'app.sml.musicVideo.top.cutCopy', defaultMessage: 'Copy this cut' },
  cutFailed: { id: 'app.sml.musicVideo.top.cutFailed', defaultMessage: 'The new cut was not made. Try again.' },
  saveSaved: { id: 'app.sml.musicVideo.save.saved', defaultMessage: 'Saved' },
  saveDirty: { id: 'app.sml.musicVideo.save.dirty', defaultMessage: 'Not saved yet' },
  saveSaving: { id: 'app.sml.musicVideo.save.saving', defaultMessage: 'Saving' },
  saveFailed: {
    id: 'app.sml.musicVideo.save.failed',
    defaultMessage: 'Not saved. We try again when you change something.',
  },
  saveInvalid: {
    id: 'app.sml.musicVideo.save.invalid',
    defaultMessage: 'This change cannot be saved. Undo it to keep saving.',
  },
  saveStale: {
    id: 'app.sml.musicVideo.save.stale',
    defaultMessage: 'Another change came in. We loaded the newest cut.',
  },
  saveWorking: { id: 'app.sml.musicVideo.save.working', defaultMessage: 'The AI writes this cut now.' },
  limitClips: {
    id: 'app.sml.musicVideo.limit.clips',
    defaultMessage: 'This cut has the most clips it can hold.',
  },
  limitSize: {
    id: 'app.sml.musicVideo.limit.size',
    defaultMessage: 'This cut is as large as it can be. Remove a clip or a text first.',
  },
  saveReadOnly: { id: 'app.sml.musicVideo.save.readOnly', defaultMessage: 'Example artist · nothing is saved' },
  planChip: { id: 'app.sml.musicVideo.top.planChip', defaultMessage: '{plan} plan' },
  credits: { id: 'app.sml.musicVideo.top.credits', defaultMessage: 'Credits <b>{count}</b>' },
  creditsNoLimit: { id: 'app.sml.musicVideo.top.creditsNoLimit', defaultMessage: 'Credits <b>no limit</b>' },
  watch: { id: 'app.sml.musicVideo.top.watch', defaultMessage: 'Watch full screen' },
  make: {
    id: 'app.sml.musicVideo.top.make',
    defaultMessage: 'Make the video · {credits, plural, one {# credit} other {# credits}}',
  },
  makeLocked: { id: 'app.sml.musicVideo.top.makeLocked', defaultMessage: 'Make the video' },
  makeShort: { id: 'app.sml.musicVideo.top.makeShort', defaultMessage: 'Make · {credits}' },
  lockedLabel: { id: 'app.sml.musicVideo.top.lockedLabel', defaultMessage: 'On Pro' },

  // The rail
  toolsLabel: { id: 'app.sml.musicVideo.tools.label', defaultMessage: 'Tools' },
  toolFootage: { id: 'app.sml.musicVideo.tools.footage', defaultMessage: 'Footage' },
  toolText: { id: 'app.sml.musicVideo.tools.text', defaultMessage: 'Text' },
  toolLyrics: { id: 'app.sml.musicVideo.tools.lyrics', defaultMessage: 'Lyrics' },
  toolLook: { id: 'app.sml.musicVideo.tools.look', defaultMessage: 'Look' },

  // The preview and its transport (b1, spec §4.6)
  backBar: { id: 'app.sml.musicVideo.transport.backBar', defaultMessage: 'Back one bar' },
  nextBar: { id: 'app.sml.musicVideo.transport.nextBar', defaultMessage: 'Next bar' },
  play: { id: 'app.sml.musicVideo.transport.play', defaultMessage: 'Play' },
  pause: { id: 'app.sml.musicVideo.transport.pause', defaultMessage: 'Pause' },
  live: { id: 'app.sml.musicVideo.transport.live', defaultMessage: 'Live preview · no credits' },
  safeArea: { id: 'app.sml.musicVideo.transport.safeArea', defaultMessage: 'Safe area' },
  ratio: { id: 'app.sml.musicVideo.transport.ratio', defaultMessage: '16:9' },
  noSound: {
    id: 'app.sml.musicVideo.transport.noSound',
    defaultMessage: 'We still read the song file. The preview plays with no sound until then.',
  },
  tagBank: { id: 'app.sml.musicVideo.preview.tagBank', defaultMessage: 'Bank · {label}' },
  tagYours: { id: 'app.sml.musicVideo.preview.tagYours', defaultMessage: 'Yours · {label}' },
  tagAi: { id: 'app.sml.musicVideo.preview.tagAi', defaultMessage: 'AI · {label}' },
  tagLoading: { id: 'app.sml.musicVideo.preview.tagLoading', defaultMessage: 'Loading the clip' },
  previewLabel: { id: 'app.sml.musicVideo.preview.label', defaultMessage: 'Preview of the music video' },
  dropReplace: { id: 'app.sml.musicVideo.preview.dropReplace', defaultMessage: 'Replace clip {number}' },
  dropNoSelection: {
    id: 'app.sml.musicVideo.preview.dropNoSelection',
    defaultMessage: 'Select a clip first, or drop on the timeline',
  },
  fullScreenClose: { id: 'app.sml.musicVideo.preview.close', defaultMessage: 'Close' },
  endCardLink: { id: 'app.sml.musicVideo.preview.endCardLink', defaultMessage: 'SmartLink' },

  // The leave dialog
  leaveTitle: { id: 'app.sml.musicVideo.leave.title', defaultMessage: 'Leave the editor?' },
  leaveText: {
    id: 'app.sml.musicVideo.leave.text',
    defaultMessage: 'Your last change is not saved yet. We save it before you go.',
  },
  leaveTextFailed: {
    id: 'app.sml.musicVideo.leave.textFailed',
    defaultMessage: 'Your last change did not save. If you leave now, it is lost.',
  },
  leaveConfirm: { id: 'app.sml.musicVideo.leave.confirm', defaultMessage: 'Save and leave' },
  leaveStay: { id: 'app.sml.musicVideo.leave.stay', defaultMessage: 'Stay' },
});
